<template>
    <div class="item-documents-section mt-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="field-label mb-0">
                <i class="fa fa-folder-open-o mr-2"></i> Archivos de Evidencia
            </label>
            <div class="actions">
                <b-dropdown size="sm" variant="outline-primary" no-caret right>
                    <template #button-content>
                        <i class="fa fa-plus"></i> Agregar
                    </template>
                    <b-dropdown-item @click="showLinkModal = true">
                        <i class="fa fa-link mr-2"></i> Seleccionar existente
                    </b-dropdown-item>
                    <b-dropdown-item @click="showUploadForm = true">
                        <i class="fa fa-upload mr-2"></i> Adjuntar nuevo
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <!-- Formulario de subida -->
        <div v-if="showUploadForm" class="upload-form p-3 mb-3 border rounded bg-light">
            <h6 class="mb-3">Adjuntar nuevo documento</h6>
            <div class="form-group mb-2">
                <label class="small text-muted">Archivo</label>
                <b-form-file
                    v-model="newFile"
                    placeholder="Seleccione un archivo..."
                    drop-placeholder="Arrastre un archivo aquí..."
                    size="sm"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    @input="onFileSelected"
                    browse-text="Seleccionar"
                ></b-form-file>
            </div>
            <div class="form-group mb-3">
                <label class="small text-muted">Nombre personalizado (opcional)</label>
                <input 
                    type="text" 
                    class="form-control" 
                    v-model="customName" 
                    placeholder="Ej: Evidencia de capacitación..."
                >
            </div>
            <div class="d-flex justify-content-end gap-2">
                <b-button size="sm" variant="secondary" class="mr-2" @click="cancelUpload">Cancelar</b-button>
                <b-button size="sm" variant="primary" @click="uploadDocument" :disabled="!newFile || uploading">
                    <i class="fa fa-spinner fa-spin" v-if="uploading"></i>
                    {{ uploading ? 'Subiendo...' : 'Guardar' }}
                </b-button>
            </div>
        </div>

        <!-- Lista de documentos -->
        <div class="documents-list" v-if="documents && documents.length">
            <div v-for="doc in documents" :key="doc.id" class="document-item p-2 mb-2 border rounded d-flex align-items-center bg-white">
                <div class="doc-icon mr-3 text-center" style="width: 40px;">
                    <i :class="getFileIcon(doc)" class="fa-2x"></i>
                </div>
                <div class="doc-info flex-grow-1 overflow-hidden">
                    <div class="font-weight-bold text-truncate" :title="getDocumentName(doc)">
                        {{ getDocumentName(doc) }}
                    </div>
                    <div class="small text-muted text-truncate" v-if="getDocumentName(doc) !== doc.original_name">
                        {{ doc.original_name }}
                    </div>
                </div>
                <div class="doc-actions ml-2">
                    <b-button 
                        size="sm" 
                        variant="link" 
                        class="text-primary p-1" 
                        @click="downloadDoc(doc)"
                        title="Descargar"
                        :disabled="!doc.path"
                    >
                        <i class="fa fa-download"></i>
                    </b-button>
                    <b-button 
                        size="sm" 
                        variant="link" 
                        class="text-danger p-1" 
                        @click="unlinkDoc(doc)"
                        title="Quitar de este ítem"
                    >
                        <i class="fa fa-times"></i>
                    </b-button>
                </div>
            </div>
        </div>
        <div v-else-if="!showUploadForm" class="text-center text-muted py-3 small bg-light rounded">
            No hay documentos relacionados
        </div>

        <!-- Modal para vincular existentes -->
        <b-modal
            v-model="showLinkModal"
            title="Seleccionar documentos del diagnóstico"
            size="lg"
            ok-title="Vincular seleccionados"
            cancel-title="Cancelar"
            @ok="linkSelectedDocuments"
            :ok-disabled="!canLink"
        >
            <div v-if="availableDocuments.length === 0" class="text-center py-4 text-muted">
                No hay documentos disponibles en el diagnóstico para vincular.
            </div>
            <div v-else>
                <b-form-group>
                    <b-form-checkbox-group v-model="selectedDocsToLink" stacked>
                        <div v-for="doc in availableDocuments" :key="doc.id" class="mb-2 p-2 border-bottom">
                            <div class="d-flex align-items-center">
                                <b-form-checkbox :value="doc.id" class="mr-3 mb-0">
                                    <span class="sr-only">Seleccionar</span>
                                </b-form-checkbox>
                                <i :class="getFileIcon(doc)" class="mr-2 fa-lg"></i>
                                <div>
                                    <div class="font-weight-bold">{{ getDocumentName(doc) }}</div>
                                    <small class="text-muted" v-if="getDocumentName(doc) !== doc.original_name">
                                        {{ doc.original_name }}
                                    </small>
                                </div>
                            </div>
                            
                            <!-- File input for docs without path -->
                            <div v-if="selectedDocsToLink.includes(doc.id) && !doc.path" class="mt-2 pl-5">
                                <b-form-file
                                    placeholder="Seleccione archivo para subir..."
                                    drop-placeholder="Arrastre un archivo..."
                                    size="sm"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                    @input="(file) => onModalFileSelected(doc.id, file)"
                                ></b-form-file>
                                <small class="text-danger" v-if="!filesToUpload[doc.id]">
                                    <i class="fa fa-exclamation-circle"></i> Archivo requerido
                                </small>
                            </div>
                        </div>
                    </b-form-checkbox-group>
                </b-form-group>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "ItemDocuments",
    props: {
        documents: {
            type: Array,
            default: () => []
        },
        diagnosticDocuments: {
            type: Array,
            default: () => []
        },
        itemResponseId: {
            type: [Number, String],
            required: true
        },
        companyId: {
            type: [Number, String],
            required: true
        },
        diagnosticId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            showUploadForm: false,
            showLinkModal: false,
            newFile: null,
            customName: "",
            uploading: false,
            linking: false,
            selectedDocsToLink: [], // Array of IDs
            filesToUpload: {} // Map docId -> File
        };
    },
    computed: {
        availableDocuments() {
            // Filtrar documentos que ya están en este item
            const currentIds = this.documents.map(d => d.id);
            return this.diagnosticDocuments.filter(d => 
                !currentIds.includes(d.id)
            );
        },
        canLink() {
            if (this.selectedDocsToLink.length === 0) return false;
            
            // Check if all selected docs without path have a file uploaded
            for (const docId of this.selectedDocsToLink) {
                const doc = this.availableDocuments.find(d => d.id === docId);
                if (doc && !doc.path && !this.filesToUpload[docId]) {
                    return false;
                }
            }
            return !this.linking;
        }
    },
    methods: {
        ...mapActions("skudo/diagnostics", [
            "uploadDiagnosticDocument",
            "linkDocumentToItemResponse",
            "unlinkDocumentFromItemResponse",
            "downloadDiagnosticDocument"
        ]),

        getDocumentName(doc) {
            if (doc.document_list && doc.document_list.name) return doc.document_list.name;
            if (doc.custom_name) return doc.custom_name;
            if (doc.original_name) return doc.original_name;
            return 'Documento sin nombre';
        },

        getFileIcon(file) {
            if (!file) return 'fa fa-file-o text-secondary';
            const name = file.original_name || file.path || '';
            const ext = name.split('.').pop().toLowerCase();
            
            if (['pdf'].includes(ext)) return 'fa fa-file-pdf-o text-danger';
            if (['doc', 'docx'].includes(ext)) return 'fa fa-file-word-o text-primary';
            if (['xls', 'xlsx'].includes(ext)) return 'fa fa-file-excel-o text-success';
            if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'fa fa-file-image-o text-info';
            return 'fa fa-file-o text-secondary';
        },

        onFileSelected(file) {
            if (file && !this.customName) {
                // Pre-fill custom name only if empty
                // this.customName = file.name; // User might prefer empty by default
            }
        },

        async uploadDocument() {
            if (!this.newFile) return;

            this.uploading = true;
            try {
                const formData = new FormData();
                formData.append('file', this.newFile);
                formData.append('company_id', this.companyId);
                formData.append('diagnostic_id', this.diagnosticId);
                formData.append('item_response_id', this.itemResponseId);
                
                if (this.customName) {
                    formData.append('custom_name', this.customName);
                }

                await this.uploadDiagnosticDocument(formData);

                this.$alertSuccess('Documento subido correctamente');
                this.cancelUpload();
                this.$emit('change');
            } catch (error) {
                this.$alertError('Error al subir documento');
                console.error(error);
            } finally {
                this.uploading = false;
            }
        },

        cancelUpload() {
            this.showUploadForm = false;
            this.newFile = null;
            this.customName = "";
        },

        onModalFileSelected(docId, file) {
            if (file) {
                this.$set(this.filesToUpload, docId, file);
            } else {
                this.$delete(this.filesToUpload, docId);
            }
        },

        async linkSelectedDocuments(bvModalEvent) {
            if (!this.canLink) return;

            this.linking = true;
            // Prevent modal close
            bvModalEvent.preventDefault();

            try {
                const promises = this.selectedDocsToLink.map(docId => {
                    const doc = this.availableDocuments.find(d => d.id === docId);
                    
                    if (doc.path) {
                        // Case A: Existing file, just link
                        return this.linkDocumentToItemResponse({
                            documentId: docId,
                            itemResponseId: this.itemResponseId,
                            companyId: this.companyId
                        });
                    } else if (this.filesToUpload[docId]) {
                        // Case B: No file, upload new one and link
                        const formData = new FormData();
                        formData.append('file', this.filesToUpload[docId]);
                        formData.append('company_id', this.companyId);
                        formData.append('item_response_id', this.itemResponseId);
                        formData.append('diagnostic_id', this.diagnosticId);
                        if (doc.document_list_id) {
                            formData.append('document_list_id', doc.document_list_id);
                        }
                        // Use existing name logic if needed, but backend uses original name usually
                        // We could pass custom_name if we wanted to copy it from the 'requirement' doc
                        if (doc.custom_name) {
                            formData.append('custom_name', doc.custom_name);
                        }

                        return this.uploadDiagnosticDocument(formData).then((response) => {
                            return this.linkDocumentToItemResponse({
                                documentId: response.data.id,
                                itemResponseId: this.itemResponseId,
                                companyId: this.companyId
                            });
                        });
                    }
                });

                await Promise.all(promises);

                this.$alertSuccess('Documentos vinculados correctamente');
                this.selectedDocsToLink = [];
                this.filesToUpload = {};
                this.showLinkModal = false;
                this.$emit('change');
            } catch (error) {
                this.$alertError('Error al vincular documentos');
                console.error(error);
            } finally {
                this.linking = false;
            }
        },

        async unlinkDoc(doc) {
            try {
                await this.$confirm(
                    '¿Deseas quitar este documento de la lista de evidencias de este ítem? El documento permanecerá en el diagnóstico.',
                    'Quitar relación',
                    {
                        confirmButtonText: 'Sí, quitar',
                        cancelButtonText: 'Cancelar',
                        type: 'warning'
                    }
                );

                await this.unlinkDocumentFromItemResponse({
                    documentId: doc.id,
                    companyId: this.companyId
                });

                this.$alertSuccess('Documento desvinculado');
                this.$emit('change');
            } catch (error) {
                if (error !== 'cancel') {
                     this.$alertError('Error al desvincular el documento');
                }
            }
        },

        async downloadDoc(doc) {
            try {
                const response = await this.downloadDiagnosticDocument({
                    documentId: doc.id,
                    companyId: this.companyId
                });

                if (response && response.data) {
                    const { base64, mime_type, original_name } = response.data;
                    const byteCharacters = atob(base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: mime_type });
                    
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = doc.custom_name || doc.original_name || original_name || 'documento';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }
            } catch (error) {
                this.$alertError('Error al descargar');
            }
        }
    }
};
</script>

<style scoped>
.field-label {
    font-size: 14px;
    font-weight: 600;
    color: #1F2937;
}

.document-item {
    transition: background-color 0.2s;
}

.document-item:hover {
    background-color: #f9fafb !important;
}

.doc-icon i {
    color: #6B7280;
}
</style>
